import {
  Checkbox,
  makeStyles,
  Radio,
  tokens,
} from '@fluentui/react-components';
import type { ICardData } from '@moko/utils';
import { FieldIcon, formatEntryText } from './cardRendering';
import { StatusBadge, type IStatusOption } from '../StatusBadge';

export const STATIC_STATUS_ENTRY_KEY = 'static-status';

const useStyles = makeStyles({
  card: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  cardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    flexGrow: 1,
    minWidth: 0,
  },
  blockSlot: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 0,
  },
  inlineSlot: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: '6px',
    minWidth: 0,
  },
  inlineEntry: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
    minWidth: 0,
  },
  separator: {
    color: tokens.colorNeutralForeground4,
  },
  fieldValue: {
    lineHeight: '20px',
    overflowWrap: 'anywhere',
  },
  labelledEntry: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },
  entryLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
  },
});

const ALIGN_MAP: Record<string, string> = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

interface EntityCardProps {
  data: ICardData;
  onClick?: (id: number, event?: React.MouseEvent<HTMLElement>) => void;
  selectable?: boolean;
  selected?: boolean;
  onSelect?: (id: number) => void;
  multi?: boolean;
  statuses?: readonly IStatusOption[];
  showFieldLabels?: boolean;
}

const EntityCard = ({
  data,
  onClick,
  selectable,
  selected,
  onSelect,
  multi,
  statuses,
  showFieldLabels = true,
}: EntityCardProps) => {
  const styles = useStyles();
  const slots = data.slots;

  const renderEntryValue = (
    entry: ICardData['slots'][number]['entries'][number]
  ) => {
    if (entry.key === STATIC_STATUS_ENTRY_KEY && statuses?.length) {
      const statusId = Number(entry.value);
      if (!Number.isNaN(statusId)) {
        return <StatusBadge statuses={statuses} statusId={statusId} />;
      }
    }
    return <span className={styles.fieldValue}>{formatEntryText(entry)}</span>;
  };

  return (
    <div className={styles.card} onClick={(e) => onClick?.(data.id, e)}>
      {selectable && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{ marginTop: -2, flexShrink: 0 }}
        >
          {multi ? (
            <Checkbox checked={selected} onChange={() => onSelect?.(data.id)} />
          ) : (
            <Radio checked={selected} onChange={() => onSelect?.(data.id)} />
          )}
        </div>
      )}
      <div className={styles.cardBody}>
        {slots.map((slot) => {
          const isInline = slot.display === 'inline';
          const sharedStyle = {
            fontSize: slot.fontSize,
            fontWeight: slot.fontWeight,
            color: slot.color ?? undefined,
            textAlign: slot.align as 'left' | 'center' | 'right',
          };

          if (isInline) {
            return (
              <div
                key={slot.slotId}
                className={styles.inlineSlot}
                style={{
                  ...sharedStyle,
                  justifyContent: ALIGN_MAP[slot.align] ?? 'flex-start',
                }}
              >
                {slot.entries.map((entry, i) => (
                  <div key={entry.key} className={styles.inlineEntry}>
                    <span className={styles.labelledEntry}>
                      {showFieldLabels && entry.label && (
                        <span className={styles.entryLabel}>{entry.label}</span>
                      )}
                      <span className={styles.inlineEntry}>
                        <FieldIcon
                          icon={entry.icon}
                          fontSize={slot.fontSize}
                          fontWeight={slot.fontWeight}
                        />
                        {renderEntryValue(entry)}
                      </span>
                    </span>
                    {i < slot.entries.length - 1 && (
                      <span className={styles.separator}>|</span>
                    )}
                  </div>
                ))}
              </div>
            );
          }

          return (
            <div
              key={slot.slotId}
              className={styles.blockSlot}
              style={sharedStyle}
            >
              {slot.entries.map((entry) => (
                <div key={entry.key} className={styles.labelledEntry}>
                  {showFieldLabels && entry.label && (
                    <span className={styles.entryLabel}>{entry.label}</span>
                  )}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 4,
                    }}
                  >
                    <FieldIcon
                      icon={entry.icon}
                      fontSize={slot.fontSize}
                      fontWeight={slot.fontWeight}
                    />
                    {renderEntryValue(entry)}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EntityCard;
